import { motion } from "framer-motion";
import { Image, Frame } from "lucide-react";
import AnimatedCounter from "./AnimatedCounter";

interface GalleriesSlideProps {
  count: number;
}

const GalleriesSlide = ({ count }: GalleriesSlideProps) => {
  if (count === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <Frame className="w-16 h-16 text-purple-400" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-background mb-4"
        >
          Beyond the Stroll
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-background/70"
        >
          You found art in unexpected places this year!
        </motion.p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center px-6">
      {/* Decorative frame */}
      <motion.div
        initial={{ scale: 0, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 150 }}
        className="relative mb-6"
      >
        <div className="w-32 h-40 border-4 border-purple-400 rounded-lg relative overflow-hidden">
          <motion.div
            className="absolute inset-2 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded"
            animate={{
              backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "linear",
            }}
          />
          <Image className="w-12 h-12 text-purple-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        
        {/* Frame corners */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute -top-2 -left-2 w-4 h-4 border-t-4 border-l-4 border-secondary rounded-tl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="absolute -top-2 -right-2 w-4 h-4 border-t-4 border-r-4 border-secondary rounded-tr"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="absolute -bottom-2 -left-2 w-4 h-4 border-b-4 border-l-4 border-secondary rounded-bl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="absolute -bottom-2 -right-2 w-4 h-4 border-b-4 border-r-4 border-secondary rounded-br"
        />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-xl text-background/80 mb-2"
      >
        You attended
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      >
        <AnimatedCounter
          value={count}
          className="text-7xl md:text-8xl font-bold text-purple-400"
        />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-2xl text-background mt-2"
      >
        {count === 1 ? "stroll" : "strolls"}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-background/60 mt-4"
      >
        Exploring art in the wild! 🚶
      </motion.p>
    </div>
  );
};

export default GalleriesSlide;
